import { useState, useEffect } from "react";

export function useTheme() {
  // Initialize theme from DOM (which was set by the script in index.html)
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Read current theme from DOM class
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    // Check if user has manually set a theme preference
    const hasUserPreference = localStorage.getItem('theme-user-set') === 'true';
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    
    // If user has never set a preference, always follow system theme
    // If user has set a preference, use their saved choice
    const currentTheme = hasUserPreference ? savedTheme : systemTheme;
    
    // Only update if different from current state
    if (currentTheme && currentTheme !== theme) {
      setTheme(currentTheme);
      updateDomTheme(currentTheme);
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only apply system theme if user has never set a preference
      const hasUserPref = localStorage.getItem('theme-user-set') === 'true';
      if (!hasUserPref) {
        const newTheme = e.matches ? 'dark' : 'light';
        setTheme(newTheme);
        updateDomTheme(newTheme);
        console.log(`System theme changed to ${newTheme}`);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme]);

  const updateDomTheme = (newTheme: 'light' | 'dark') => {
    const root = document.documentElement;
    if (newTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    // Save user's choice and mark that they have set a preference
    localStorage.setItem('theme', newTheme);
    localStorage.setItem('theme-user-set', 'true');
    
    updateDomTheme(newTheme);
    console.log(`Theme manually changed to ${newTheme} (user preference saved)`);
  };

  return { theme, toggleTheme };
}